/**
 * Shared WCAG color utilities for the Punchup tag and color-picker primitives.
 * Not a component — import directly from this file.
 *
 * The hex channel maths (luminance, darken) live in ./colorMath — the single source
 * shared with the chart palette, so a tag's readable shade and a chart's contrast
 * outline derive from the same primitives.
 */
import { wcagLuminance, darkenHex } from "./colorMath";

/**
 * Display color for a tag chip on a soft-label background (~15% tint).
 * Very light colors (luminance > 0.6) are darkened 30% to stay readable against the
 * near-white tint; darker colors pass through unchanged. Used by PunchupTag.
 */
export function chipColor(hex: string): string {
  // Normalize first so 3-digit shorthand (#abc) is expanded to #AABBCC — wcagLuminance/darkenHex pad
  // rather than expand, so a raw shorthand would otherwise be read as #abc000.
  const norm = normalizeHex(hex);
  return wcagLuminance(norm) <= 0.6 ? hex : darkenHex(norm, 0.3);
}

/**
 * '#000000' or '#ffffff' — whichever gives WCAG ≥ 4.5:1 contrast against a solid
 * background of the given color. Used by PunchupColorPicker.
 */
export function contrastColor(hex: string): string {
  // Normalize first so 3-digit shorthand is expanded before the luminance check (see chipColor).
  return wcagLuminance(normalizeHex(hex)) > 0.179 ? "#000000" : "#ffffff";
}

/** True when `s` is a valid 3- or 6-digit hex color, with or without a leading '#'. */
export function isValidHex(s: string): boolean {
  return /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s.trim());
}

/**
 * Canonicalizes a hex string to an uppercase 6-digit `#RRGGBB`: expands 3-digit
 * shorthand (#abc → #AABBCC) and prepends '#'. Call only on a value that passes
 * `isValidHex`.
 */
export function normalizeHex(s: string): string {
  let c = s.trim().replace("#", "");
  if (c.length === 3) {
    c = c
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }
  return `#${c.toUpperCase()}`;
}

/** HSV color: hue in [0, 360), saturation and value (brightness) in [0, 1]. */
export interface Hsv {
  h: number;
  s: number;
  v: number;
}

/**
 * Converts a hex color to HSV for the color-picker area (saturation/brightness square +
 * hue slider). Pass a value that passes `isValidHex`; greys return `s = 0` so the caller
 * can preserve the existing hue rather than snapping to red.
 */
export function hexToHsv(hex: string): Hsv {
  const c = normalizeHex(hex).replace("#", "");
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

/** Converts HSV back to an uppercase `#RRGGBB` hex. Inverse of `hexToHsv`. */
export function hsvToHex({ h, s, v }: Hsv): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}
